/** Captura para tour: regiões estruturadas (hero / kpis / cards) + prints compactos. */

import type { TourAmbiente } from './tourEnv';
import {
  extractPageRegions,
  hashRegions,
  type TourRegion,
} from './pageRegions';

export type CapturedUiElement = {
  selector: string;
  label: string;
  role?: string;
  kind?: string;
};

export type PageCapture = {
  /** Preferido pelo backend */
  regions: TourRegion[];
  /** Legado (derivado das regiões) */
  elements: CapturedUiElement[];
  contentHash: string;
  pageTitle: string;
  /** Página completa (JPEG compacto) */
  screenshotBase64?: string;
  /** HTML compacto do main/conteúdo (contexto textual) */
  pageHtml?: string;
  ambiente: TourAmbiente;
};

/** Limites para não estourar body (15mb) nem tokens do Gemini */
const FULL_PAGE = {
  scale: 0.28,
  quality: 0.42,
  maxChars: 280_000,
  maxW: 960,
  maxH: 1400,
};
const BLOCK = {
  scale: 0.35,
  quality: 0.42,
  maxChars: 100_000,
  maxW: 720,
  maxH: 900,
  maxBlocks: 16,
};

function cleanLabel(text: string): string {
  return text.replace(/\s+/g, ' ').trim().slice(0, 120);
}

function regionsToElements(regions: TourRegion[]): CapturedUiElement[] {
  return regions.map((r) => ({
    selector: r.selector,
    label: r.title,
    role: 'region',
    kind: r.kind,
  }));
}

async function loadHtml2Canvas() {
  const { default: html2canvas } = await import('html2canvas');
  return html2canvas;
}

/**
 * Print JPEG compacto de um elemento.
 * Reduz resolução/qualidade e corta se ainda passar do teto.
 */
async function captureElementJpeg(
  el: HTMLElement,
  opts: {
    scale: number;
    quality: number;
    maxChars: number;
    maxW: number;
    maxH: number;
  },
): Promise<string | undefined> {
  try {
    const html2canvas = await loadHtml2Canvas();
    const rect = el.getBoundingClientRect();
    if (rect.width < 40 || rect.height < 30) return undefined;

    const canvas = await html2canvas(el, {
      scale: opts.scale,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: Math.min(Math.ceil(rect.width), opts.maxW),
      height: Math.min(Math.ceil(rect.height), opts.maxH),
      windowWidth: Math.min(window.innerWidth, opts.maxW + 80),
      windowHeight: Math.min(window.innerHeight, opts.maxH + 80),
    });

    let quality = opts.quality;
    let dataUrl = canvas.toDataURL('image/jpeg', quality);
    // Rebaixa qualidade se ainda grande
    while (dataUrl.length > opts.maxChars && quality > 0.22) {
      quality -= 0.08;
      dataUrl = canvas.toDataURL('image/jpeg', quality);
    }
    if (!dataUrl || dataUrl.length < 80 || dataUrl.length > opts.maxChars) {
      return undefined;
    }
    return dataUrl;
  } catch {
    return undefined;
  }
}

function mainRoot(): HTMLElement {
  return (
    (document.querySelector(
      'main, .admin-page, .page-content, [role="main"]',
    ) as HTMLElement | null) || document.body
  );
}

async function captureFullPageJpeg(): Promise<string | undefined> {
  return captureElementJpeg(mainRoot(), FULL_PAGE);
}

const HTML_PAGE_MAX = 14_000;
const HTML_BLOCK_MAX = 3_500;

/** HTML enxuto: remove scripts/svgs e atributos ruidosos; mantém data-tour + texto. */
function compactHtml(el: HTMLElement, maxChars: number): string {
  try {
    const clone = el.cloneNode(true) as HTMLElement;
    clone
      .querySelectorAll(
        'script, style, noscript, svg, canvas, iframe, video, audio, link, meta',
      )
      .forEach((n) => n.remove());

    const walker = document.createTreeWalker(clone, NodeFilter.SHOW_ELEMENT);
    const nodes: Element[] = [];
    let cur = walker.nextNode();
    while (cur) {
      nodes.push(cur as Element);
      cur = walker.nextNode();
    }

    const keepAttr =
      /^(data-tour|aria-label|role|href|alt|title|type|placeholder|name)$/i;
    for (const node of nodes) {
      const attrs = Array.from(node.attributes);
      for (const attr of attrs) {
        if (attr.name === 'class') {
          // só classes úteis / curtas
          const useful = attr.value
            .split(/\s+/)
            .filter(
              (c) =>
                c.length > 1 &&
                c.length < 40 &&
                /^(master-|page-|btn|card|hero|kpi|stat|panel)/i.test(c),
            )
            .slice(0, 4)
            .join(' ');
          if (useful) node.setAttribute('class', useful);
          else node.removeAttribute('class');
          continue;
        }
        if (!keepAttr.test(attr.name)) node.removeAttribute(attr.name);
      }
    }

    let html = clone.outerHTML
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/\s+/g, ' ')
      .replace(/>\s+</g, '><')
      .trim();

    if (html.length > maxChars) {
      html = `${html.slice(0, maxChars)}…`;
    }
    return html;
  } catch {
    return (el.innerText || '').replace(/\s+/g, ' ').trim().slice(0, maxChars);
  }
}

/** Print + HTML de cada região (ordem do tour). */
async function enrichRegionsForAi(
  regions: TourRegion[],
): Promise<TourRegion[]> {
  const out: TourRegion[] = [];
  let shotCount = 0;
  for (const region of regions) {
    const el = document.querySelector<HTMLElement>(region.selector);
    if (!el) {
      out.push(region);
      continue;
    }
    try {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    } catch {
      /* ignore */
    }

    const htmlSnippet = compactHtml(el, HTML_BLOCK_MAX);
    let screenshotBase64: string | undefined;
    if (shotCount < BLOCK.maxBlocks) {
      screenshotBase64 = await captureElementJpeg(el, BLOCK);
      if (screenshotBase64) shotCount += 1;
    }
    out.push({ ...region, htmlSnippet, screenshotBase64 });
  }
  return out;
}

export async function capturePageMap(options?: {
  withScreenshot?: boolean;
  ambiente?: TourAmbiente;
}): Promise<PageCapture> {
  const ambiente: TourAmbiente = options?.ambiente || 'APP';
  const withShot = options?.withScreenshot === true;

  let regions: TourRegion[] = [];
  if (ambiente === 'APP') {
    regions = extractPageRegions(document);
  } else {
    document.querySelectorAll<HTMLElement>('[data-tour-sim]').forEach((node) => {
      const id = node.getAttribute('data-tour-sim');
      if (!id) return;
      regions.push({
        id,
        kind: 'sim',
        title: cleanLabel(node.getAttribute('aria-label') || node.innerText || id),
        selector: `[data-tour-sim="${id}"]`,
        summary: id,
      });
    });
  }

  const pageTitle =
    cleanLabel(document.title.replace(/\|.*$/, '').trim()) ||
    regions.find((r) => r.kind === 'hero')?.title ||
    'Página';
  const contentHash = hashRegions(regions);

  let screenshotBase64: string | undefined;
  let pageHtml: string | undefined;
  if (withShot) {
    const root = mainRoot();
    pageHtml = compactHtml(root, HTML_PAGE_MAX);
    screenshotBase64 = await captureFullPageJpeg();
    regions = await enrichRegionsForAi(regions);
  }

  return {
    regions,
    elements: regionsToElements(regions),
    contentHash,
    pageTitle,
    screenshotBase64,
    pageHtml,
    ambiente,
  };
}

export function pageTourId(urlPath: string): string {
  const clean = (urlPath.split('?')[0] || '/').replace(/\/+$/, '') || '/';
  return `page:${clean}`;
}

/** Reexport para testes / consumidores */
export { extractPageRegions, hashRegions };
export type { TourRegion };
